import { ACCOUNT_STATUS } from '$lib/stores/user/constants';
import type { UserStoreState } from '$lib/stores/user/store';
import { has2_0Account, is2_0OriginatingUser } from '$lib/utils/aofl-accounts';
import {
	AOFL_PRODUCT_NAMES,
	PAGES,
	QUERY_PARAM_KEYS,
	REG_PATH_KEYS,
	REG_PATH_STEPS,
} from '$lib/utils/constants';
import { Sentry } from '$lib/services/sentry';
import { get } from 'svelte/store';
import { app } from '$lib/stores/app/store';

class PostAuthenticationPathService {
	/**
	 * Whether the user has completed the given reg-path step or any step after it on their subscription path
	 * @param {import('$lib/stores/user/store').UserStoreState} user - Current user store state (uses `userMetaData.subscription_path` and `completed_step`).
	 * @param {string} step - Reg-path step key to test.
	 * @returns {boolean} True if `completed_step` is this step or a later step in path order; false if unknown path, step not on path, or not yet reached.
	 */
	hasCompletedRegPathStep(user: UserStoreState, step: string): boolean {
		const { subscription_path, completed_step } = user.userMetaData;
		if (!subscription_path || !completed_step) {
			return false;
		}

		const productName = has2_0Account()
			? AOFL_PRODUCT_NAMES.ABCMOUSE_2_0
			: AOFL_PRODUCT_NAMES.ABCMOUSE;
		const pathMap = SUBSCRIPTION_PATH_MAP[productName];
		const steps = pathMap[subscription_path as keyof typeof pathMap];
		if (!steps) {
			return false;
		}

		const stepKeys = Object.keys(steps);
		const targetIndex = stepKeys.indexOf(step);
		if (targetIndex === -1) {
			return false;
		}

		const completedIndex = stepKeys.indexOf(completed_step);
		if (completedIndex === -1) {
			return false;
		}

		return completedIndex >= targetIndex;
	}

	/**
	 * Gets the path a user should be redirected to after authentication, or after completing a step in the subscription path.
	 * @param {UserStoreState} user - The user store state
	 * @returns {string} The path the user should be redirected to
	 */
	getRedirectPath(user: UserStoreState): string {
		const url = new URLSearchParams(window?.location?.search);
		const gotoPath = url.get(QUERY_PARAM_KEYS.GOTO);
		if (gotoPath) {
			return gotoPath;
		}

		const accountStatus: ACCOUNT_STATUS = user.accountStatus;
		switch (accountStatus) {
			case ACCOUNT_STATUS.ACTIVE:
				return this.getActivePath(user);
			case ACCOUNT_STATUS.AUXILIARY_ONLY_ACTIVE:
				return this.getAuxiliaryOnlyActivePath();
			case ACCOUNT_STATUS.INACTIVE:
				return this.getInactivePath();
			case ACCOUNT_STATUS.NONE:
				return this.getNonePath(user);
			case ACCOUNT_STATUS.FREE:
				return PAGES.INTERNAL.SUBSCRIPTION;
			default:
				this.trackDefaultCase('Error: default account status', user);
				return PAGES.INTERNAL.HOME;
		}
	}

	private getActivePath(user: UserStoreState): string {
		const nextSubscriptionPath = this.getNextSubscriptionPath(user);
		if (nextSubscriptionPath) {
			return nextSubscriptionPath;
		} else {
			// TODO: Find out if this error message should be logged for both 1.0 and 2.0 flows, or just 1.0.
			this.issueException('Error: nextPage is not available for this user', false, user);
			const defaultPath = this.getDefaultPath();
			return defaultPath;
		}
	}

	private getAuxiliaryOnlyActivePath(): string {
		// ABC2-15207: tmp fix for aux only users ends up in a redirect loop because GetCompletedStep clears out completed_step
		return has2_0Account() ? PAGES.INTERNAL.DOWNLOAD_APP : PAGES.INTERNAL.INSIDE_PAYWALL;
	}

	private getNextSubscriptionPath(user: UserStoreState): string | null {
		const { subscription_path, completed_step } = user.userMetaData;
		if (!subscription_path) {
			return null;
		}
		const productName = has2_0Account()
			? AOFL_PRODUCT_NAMES.ABCMOUSE_2_0
			: AOFL_PRODUCT_NAMES.ABCMOUSE;
		const pathMap = SUBSCRIPTION_PATH_MAP[productName];
		const steps = pathMap[subscription_path];
		if (!steps) {
			return null;
		}

		const stepKeys = Object.keys(steps);
		if (!completed_step || !stepKeys.includes(completed_step)) {
			return steps[stepKeys[0]];
		} else {
			const lastCompletedStepIndex = stepKeys.indexOf(completed_step);
			const nextStepIndex = lastCompletedStepIndex + 1;
			return steps[stepKeys[nextStepIndex]];
		}
	}

	private getInactivePath(): string {
		return PAGES.INTERNAL.REACTIVATION;
	}

	private getNonePath(user: UserStoreState): string {
		if (user.tokenValid) {
			this.issueException('Error: "valid" token user with "none" account status', true, user);
		}
		return PAGES.INTERNAL.HOME;
	}

	private issueException(message: string, isExternal: boolean, user: UserStoreState): void {
		Sentry.captureException(new Error(message), {
			level: isExternal ? 'error' : 'warning',
			extra: { action: 'post-auth-path:next-path-exception', user, isExternal },
		});
	}

	private async trackDefaultCase(message: string, user: UserStoreState): Promise<void> {
		Sentry.captureException(new Error(message), {
			level: 'warning',
			extra: {
				action: 'post-auth-path:default-case',
				source: 'PostAuthenticationPathService',
				destination: PAGES.INTERNAL.HOME,
				accountStatus: user.accountStatus,
			},
		});
	}

	private getDefaultPath(): string {
		const isInApp = get(app).meta.inApp;
		if (isInApp) {
			return is2_0OriginatingUser() ? PAGES.INTERNAL.DOWNLOAD_APP : PAGES.INTERNAL.INSIDE_PAYWALL;
		} else {
			return has2_0Account() ? PAGES.INTERNAL.DOWNLOAD_APP : PAGES.INTERNAL.INSIDE_PAYWALL;
		}
	}
}

/**
 * @description Users for different products can be on different subscription paths after account creation or authentication.
 * This map is used to determine the next step in the subscription path for a user.
 * The map is structured as follows:
 * {
 *  productName: {
 *      subscriptionPath: {
 *          step: page
 *      }
 *  }
 * }
 * NOTE: The step keys are ordered from the first step to the last step. Order matters.
 */
const SUBSCRIPTION_PATH_MAP = {
	[AOFL_PRODUCT_NAMES.ABCMOUSE]: {
		[REG_PATH_KEYS.DEFAULT_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.DEFAULT_RESUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		// Adding this path for historical support for migrated users.
		[REG_PATH_KEYS.NO_AA_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.IN_APP_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.IN_APP_RESUBSCRIBE]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.GOOGLE_IN_APP_SUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.IN_APP_UCB_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.IN_APP_UCB_RESUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.SUBSCRIBER_NO_PATH]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.RESUBSCRIBER_NO_PATH]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.IN_APP_SAMSUNG_SUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.IN_APP_SAMSUNG_RESUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
	},
	[AOFL_PRODUCT_NAMES.ABCMOUSE_2_0]: {
		[REG_PATH_KEYS.DEFAULT_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.TUTOR_OFFER]: PAGES.INTERNAL.TUTOR_OFFER,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.DEFAULT_RESUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.TUTOR_OFFER]: PAGES.INTERNAL.TUTOR_OFFER,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		// This user subscribes to a 2.0 product so it has2_0Account true
		[REG_PATH_KEYS.IOS_ALT_PAYMENT_SUBSCRIBER_1_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		// This user resubscribes to a 2.0 product so it has2_0Account true
		[REG_PATH_KEYS.IOS_ALT_PAYMENT_RESUBSCRIBER_1_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.IOS_ALT_PAYMENT_SUBSCRIBER_2_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.TUTOR_OFFER]: PAGES.INTERNAL.TUTOR_OFFER,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.IOS_ALT_PAYMENT_RESUBSCRIBER_2_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		// This user subscribes to a 1.0 product so it has2_0Account true
		[REG_PATH_KEYS.ANDROID_ALT_PAYMENT_SUBSCRIBER_1_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		// This user resubscribes to a 1.0 product so it has2_0Account true
		[REG_PATH_KEYS.ANDROID_ALT_PAYMENT_RESUBSCRIBER_1_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL,
		},
		[REG_PATH_KEYS.ANDROID_ALT_PAYMENT_SUBSCRIBER_2_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.TUTOR_OFFER]: PAGES.INTERNAL.TUTOR_OFFER,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.ANDROID_ALT_PAYMENT_RESUBSCRIBER_2_0]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.STARTER_PACK]: PAGES.INTERNAL.STARTER_PACK,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.DEFAULT_IN_APP_CONSUMPTION_2_0]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.DEFAULT_IN_APP_CONSUMPTION_RESUBSCRIBE_2_0]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.SUBSCRIBER_NO_PATH]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.RESUBSCRIBER_NO_PATH]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.SPECIAL_OFFER_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
		[REG_PATH_KEYS.SPECIAL_OFFER_SUBSCRIBER_NO_SEMI_ANNUAL]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.DOWNLOAD_APP,
		},
	},
};

export const postAuthenticationPathService = new PostAuthenticationPathService();
